import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Seo from "@/components/Seo";
import { FadeIn, Eyebrow } from "@/components/Reveal";
import { quoteLink } from "@/data/siteData";
import { useLanguage, useLocalizedContent } from "@/i18n/LanguageContext";

function PolicyItem({ policy }) {
  const { t } = useLanguage();
  return (
    <div className="border-t border-slate-200 py-6 first:border-t-0 first:pt-0">
      <h4 className="font-heading text-base font-semibold text-navy sm:text-lg">{policy.name}</h4>
      <p className="mt-2 text-sm leading-relaxed text-slate-600">{policy.covers}</p>
      <p className="mt-3 text-sm text-slate-700">
        <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-700">{t("co.whoNeeds")} </span>
        {policy.who}
      </p>
    </div>
  );
}

export default function CommercialInsurance() {
  const location = useLocation();
  const { t } = useLanguage();
  const { categories } = useLocalizedContent();

  useEffect(() => {
    if (location.hash) {
      const timer = setTimeout(() => {
        document.querySelector(location.hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  return (
    <div data-testid="commercial-insurance-page">
      <Seo
        title="Commercial Insurance — Liability, Engineering, Marine | Wisdom Insurance Broking Pune"
        description="Commercial general liability, contractors all risk, erection all risk, marine cargo, property & business interruption and employee benefits insurance from an IRDAI-licensed broker in Pune."
        keywords="commercial insurance broker Pune, contractors all risk insurance, erection all risk, liability insurance India, marine cargo insurance, insurance for co-operative banks, D&O insurance India"
      />

      <section className="grain bg-navy-deep pb-16 pt-32 md:pb-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
          <FadeIn>
            <Eyebrow light>{t("co.eyebrow")}</Eyebrow>
            <h1 className="mt-4 max-w-3xl font-heading text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
              {t("co.title")}
            </h1>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
              {t("co.sub")}
            </p>
          </FadeIn>
        </div>
      </section>

      {/* Anchor jumper */}
      <nav className="sticky top-16 z-40 border-b border-slate-200 bg-white/90 backdrop-blur-xl" aria-label="Sections">
        <div className="mx-auto flex max-w-7xl gap-2 overflow-x-auto px-4 py-3 sm:px-8 lg:px-16">
          {categories.map((c) => (
            <a
              key={c.id}
              href={`#${c.id}`}
              data-testid={`anchor-${c.id}`}
              onClick={(e) => {
                e.preventDefault();
                document.getElementById(c.id)?.scrollIntoView({ behavior: "smooth" });
              }}
              className="whitespace-nowrap border border-slate-200 px-4 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:border-amber-600 hover:text-amber-700"
            >
              {c.title}
            </a>
          ))}
        </div>
      </nav>

      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        {categories.map((cat, idx) => {
          const isBanks = cat.id === "banks-financial-institutions";
          return (
            <section
              key={cat.id}
              id={cat.id}
              data-testid={`section-${cat.id}`}
              className={`scroll-mt-32 py-14 md:py-20 ${idx !== 0 ? "border-t border-slate-200" : ""}`}
            >
              {isBanks ? (
                <FadeIn>
                  <div className="grain relative mb-10 bg-navy p-8 md:p-12">
                    <Eyebrow light>{t("co.specialist")}</Eyebrow>
                    <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-white sm:text-3xl">
                      {cat.title}
                    </h2>
                    <p className="mt-4 max-w-3xl text-base leading-relaxed text-slate-300">{cat.intro}</p>
                    <Link
                      to={quoteLink(cat.enTitle)}
                      data-testid={`quote-btn-${cat.id}`}
                      className="group mt-6 inline-flex items-center gap-2 bg-amber-600 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-amber-500"
                    >
                      {t("co.getQuote")}
                      <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                  </div>
                </FadeIn>
              ) : (
                <FadeIn className="mb-10 flex flex-wrap items-start justify-between gap-6">
                  <div className="max-w-2xl">
                    <div className="flex items-center gap-3">
                      <cat.icon className="h-7 w-7 text-amber-600" strokeWidth={1.5} />
                      <span className="font-mono text-xs text-slate-400">{String(idx + 1).padStart(2, "0")}</span>
                    </div>
                    <h2 className="mt-3 font-heading text-2xl font-bold tracking-tight text-navy sm:text-3xl">{cat.title}</h2>
                    <p className="mt-3 text-base leading-relaxed text-slate-600">{cat.blurb}</p>
                  </div>
                  <Link
                    to={quoteLink(cat.enTitle)}
                    data-testid={`quote-btn-${cat.id}`}
                    className="group inline-flex items-center gap-2 bg-navy px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-amber-700"
                  >
                    {t("co.getQuote")}
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                </FadeIn>
              )}

              <div className={`grid grid-cols-1 gap-x-12 lg:grid-cols-2 ${isBanks ? "rounded-none border border-slate-200 bg-white p-6 sm:p-10" : ""}`}>
                {cat.policies.map((p) => (
                  <PolicyItem key={p.name} policy={p} />
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}
